import { useSyncExternalStore } from 'react';
import { Service, PaginatedResponse } from '@/types';
import { api } from '@/services/api';
import { useStaffDataContext } from './StaffDataContext';
import { loadAllPages } from './loadAllPages';

export interface ServiceListParams {
  page: number;
  pageSize: number;
  q?: string;
}

const buildQuery = (params: ServiceListParams) => ({
  page: params.page,
  pageSize: params.pageSize,
  q: params.q || undefined
});

export const useServicesPage = (params: ServiceListParams): PaginatedResponse<Service> => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('services', cb),
    () => 0,
    () => 0
  );
  return ctx.read('services', { kind: 'page', ...params }, (p: ServiceListParams) =>
    api.listStaffServices(buildQuery(p))
  );
};

export const useServicesAll = (): Service[] => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('services', cb),
    () => 0,
    () => 0
  );
  return ctx.read('services', { kind: 'all' }, () =>
    loadAllPages<Service>((p) => api.listStaffServices(p))
  );
};

export interface ServiceMutations {
  create: (service: Service) => Promise<void>;
  update: (service: Service) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const useServiceMutations = (): ServiceMutations => {
  const ctx = useStaffDataContext();
  const after = () => ctx.invalidate('services');
  return {
    create: async (service) => {
      await api.createService(service);
      after();
    },
    update: async (service) => {
      await api.updateService(service);
      after();
    },
    remove: async (id) => {
      await api.deleteService(id);
      after();
    }
  };
};
